import React from 'react'
import { connect } from 'react-redux'
import cx from 'classnames'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { l, Popover } from '@manychat/manyui'

import { initializeAvailableFields } from 'common/fields/linking/accountAvailableFields'
import AddCondition from 'common/filter/components/AddCondition'
import Condition from 'common/filter/components/Condition'
import { FilterSource } from 'common/filter/interfaces'
import { PopoverList } from 'components/MuiPopover'

import cm from './Filters.module.css'

const GLOBAL_OPERATORS = [
  { value: 'AND', label: l.makeTranslate('all of the following conditions') },
  { value: 'OR', label: l.makeTranslate('any of the following conditions') },
]

class Filters extends React.Component {
  static propTypes = {
    theme: PropTypes.shape({
      addConditionBtn: PropTypes.string,
      conditions: PropTypes.string,
      condition: PropTypes.string,
      operatorSelector: PropTypes.string,
      popoverTrigger: PropTypes.string,
      conditionsWrapper: PropTypes.string,
      label: PropTypes.string,
      smartSegmentWrapper: PropTypes.string,
    }),
    operatorSelectorAlwaysVisible: PropTypes.bool,
    isConditionBuilder: PropTypes.bool,
    allowFormula: PropTypes.bool,
    readOnlyGroupIndexes: PropTypes.array,
    // here groupIndex is the real index like 0, 1, 2, and the object is formed dynamically
    readOnlyItemIndexesByGroup: PropTypes.shape({
      groupIndex: PropTypes.array,
    }),

    // triggers and actions modificators
    addConditionLabel: PropTypes.string,
    allowRemove: PropTypes.bool,
    onlyOneCondition: PropTypes.bool,
    operatorSelectorAlwaysHidden: PropTypes.bool,
    useChangedLabelForOperators: PropTypes.bool,
    caption: PropTypes.string,
    botTimezone: PropTypes.string,

    //Smart Segment Controls
    renderSmartSegmentControl: PropTypes.func.isRequired,

    // different set of filters based on scenario
    scenario: PropTypes.string,

    source: PropTypes.oneOf(Object.values(FilterSource)),
  }

  static defaultProps = {
    theme: {},
    allowRemove: true,
    allowFormula: true,
    readOnlyGroupIndexes: [],
    readOnlyItemIndexesByGroup: {},
    renderSmartSegmentControl: () => null,
  }

  constructor(props, context) {
    super(props, context)
    this.state = { operatorPopoverAnchor: null }
  }

  componentDidMount() {
    this.props.dispatch(initializeAvailableFields({ scenario: this.props.scenario }))
  }

  handleOperatorChange = (e) => {
    if (!this.state.operatorPopoverAnchor) {
      return
    }
    const groupIndex = Number(this.state.operatorPopoverAnchor.dataset.index)
    const operator = e.currentTarget.dataset.value
    this.props.onOperatorChange(operator, groupIndex)
    this.setState({ operatorPopoverAnchor: null })
  }
  handleOperatorPopoverOpen = (e) => this.setState({ operatorPopoverAnchor: e.currentTarget })
  handleOperatorPopoverClose = () => this.setState({ operatorPopoverAnchor: null })

  render() {
    const { fields, segments, activeReasons, operator, groups, botTimezone } = this.props
    const { onCreateCondition, onChangeCondition, onRemoveCondition } = this.props
    const { style, className, readOnlyGroupIndexes, isConditionBuilder, isHiddenFirstGroup } =
      this.props
    const {
      operatorSelectorAlwaysVisible,
      operatorSelectorAlwaysHidden,
      theme,
      caption,
      nodeId,
      builderId,
      readOnlyItemIndexesByGroup,
      renderSmartSegmentControl,
    } = this.props

    const {
      allowRemove,
      useChangedLabelForOperators,
      allowDateFormula,
      addConditionLabel,
      onlyOneCondition,
    } = this.props

    const { operatorPopoverAnchor } = this.state
    let groupOperator
    if (operatorPopoverAnchor) {
      const groupIndex = Number(operatorPopoverAnchor.dataset.index)
      groupOperator = groups[groupIndex].operator
    }
    const multi = groups.length > 1 && !isHiddenFirstGroup

    return (
      <div className={cx(cm.wrapper, className)} style={style}>
        {groups.map((group, groupIndex) => {
          if (isHiddenFirstGroup && groupIndex === 0) return null
          const disabled = this.props.disabled || readOnlyGroupIndexes.includes(groupIndex)
          const groupOpLabel = l.getString(
            (GLOBAL_OPERATORS.find((o) => o.value === group.operator) || {}).label,
          )
          const groupCn = cx({
            [cm.multiGroup]: multi,
            [cm.firstGroup]: groupIndex === 0,
            [cm.lastGroup]: groupIndex === groups.length - 1,
            [cm.disabledGroup]: disabled,
          })
          const usedFields = group.items.reduce((res, current) => {
            if (current.field) {
              res[current.field] = current.operator
            }
            return res
          }, {})
          return (
            <div key={groupIndex} className={cx(groupCn, theme.groupCn)}>
              {multi && groupIndex < groups.length - 1 && (
                <div className={cm.globalOperator}>{operator}</div>
              )}
              {!operatorSelectorAlwaysHidden && (
                <>
                  {disabled ? (
                    <>
                      {(operatorSelectorAlwaysVisible || group.items.length > 0) && (
                        <div className={cx(cm.operatorSelector, theme.operatorSelector)}>
                          <strong>
                            {multi
                              ? l.translate(`Targeted contacts, that match `)
                              : l.translate(`Targeted only contacts, that match `)}
                            <span>{groupOpLabel}:</span>
                          </strong>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {(operatorSelectorAlwaysVisible || group.items.length > 0) && (
                        <div
                          className={cx(theme.operatorSelector, {
                            [cm.operatorSelector]: !isConditionBuilder && !caption,
                          })}
                        >
                          <span className={cx('popover-form-label m-0', theme.label)}>
                            {caption ? (
                              `${caption}`
                            ) : (
                              <>
                                {isConditionBuilder ? (
                                  <span>{l.translate('Does the contact match')}</span>
                                ) : (
                                  <>
                                    {multi ? (
                                      <span>{l.translate(`Contacts, that match `)}</span>
                                    ) : (
                                      <span>{l.translate(`Only contacts, that match `)}</span>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </span>
                          <a
                            onClick={this.handleOperatorPopoverOpen}
                            className={cx(
                              cm.popoverTrigger,
                              {
                                'text-primary p-t-0 text-subheading text-normal':
                                  isConditionBuilder || caption,
                                [cm.darkText]: !isConditionBuilder && !caption,
                              },
                              theme.popoverTrigger,
                            )}
                            data-index={groupIndex}
                          >
                            {groupOpLabel}
                            {!caption && isConditionBuilder ? '?' : ':'}
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <div className={cx(theme.conditionsWrapper)}>
                <div className={cx(cm.conditions, theme.conditions)}>
                  {group.items.map((e, i) => {
                    const disabledItem = readOnlyItemIndexesByGroup[groupIndex]?.includes(i)
                    return (
                      <Condition
                        key={e._oid || `${e.type}-${e.value}`}
                        condition={e}
                        fields={fields}
                        segments={segments}
                        activeReasons={activeReasons}
                        usedFields={usedFields}
                        groupIndex={groupIndex}
                        onRemove={onRemoveCondition}
                        onChange={onChangeCondition}
                        disabled={disabledItem || disabled}
                        className={theme.condition}
                        summaryClassName={theme.summary}
                        allowRemove={allowRemove}
                        useChangedLabelForOperators={useChangedLabelForOperators}
                        allowDateFormula={allowDateFormula}
                        isBuilder={Boolean(nodeId)}
                        builderId={builderId}
                        botTimezone={botTimezone}
                      />
                    )
                  })}
                  {!disabled && !(onlyOneCondition && get(groups, '0.items.0')) && (
                    <AddCondition
                      data-test-id={this.props['data-test-id']}
                      className={theme.addConditionBtn}
                      firstClassName={theme.addFirstConditionBtn}
                      label={addConditionLabel}
                      first={group.items.length < 1}
                      groupIndex={groupIndex}
                      onCreate={onCreateCondition}
                      fields={fields}
                      builderId={builderId}
                    />
                  )}
                </div>
                <div className={theme.smartSegmentWrapper}>{renderSmartSegmentControl()}</div>
              </div>
            </div>
          )
        })}
        <Popover
          open={operatorPopoverAnchor != null}
          anchor={operatorPopoverAnchor}
          anchorOrigin={'left top'}
          targetOrigin={'left top'}
          onRequestClose={this.handleOperatorPopoverClose}
        >
          <PopoverList>
            {GLOBAL_OPERATORS.map((op) => (
              <a key={op.value} onClick={this.handleOperatorChange} data-value={op.value}>
                <span
                  className={cx(cm.popoverEl, { [cm.popoverActiveEL]: groupOperator === op.value })}
                >
                  {l.getString(op.label)}
                </span>
              </a>
            ))}
          </PopoverList>
        </Popover>
      </div>
    )
  }
}

export default connect()(Filters)
